/**
 * Bubble View Interaction Module
 * Handles mouse/touch/keyboard events, drag-and-drop, zoom, and pan
 */

import { setBubblePosition } from "../../store/bubble-positions.js";
import * as MultiSelect from "../../ui/multi-select.js";
import { t } from "../../../../shared/i18n/index.js";
import { visibleViewRect } from "../../ui/view-centering.js";
import * as Titles from "./titles.js";

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if nodeId is a descendant of potentialAncestorId
 * @param {string} nodeId - Node ID to check
 * @param {string} potentialAncestorId - Potential ancestor ID
 * @param {Function} findFn - Function to find a node by ID
 * @returns {boolean} True if nodeId is a descendant
 */
export function isDescendant(nodeId, potentialAncestorId, findFn) {
  const ancestor = findFn(potentialAncestorId);
  if (!ancestor?.children) return false;

  function check(node) {
    if (node.id === nodeId) return true;
    if (node.children) {
      return node.children.some(check);
    }
    return false;
  }

  return ancestor.children.some(check);
}

/**
 * Convert screen coordinates to world coordinates relative to a parent bubble's center
 *
 * Coordinate system:
 * - Screen coords: mouse clientX/clientY
 * - Canvas local: screen - canvas.getBoundingClientRect().left/top
 * - World coords: (canvasLocal - pan) / zoom
 * - Node coords: relative to parent's center
 *
 * For top-level bubbles: parent center is at (cx, cy) = canvas center
 * For nested bubbles: parent center is at (cx + parent.x, cy + parent.y)
 *
 * @param {number} screenX - Screen X coordinate (clientX)
 * @param {number} screenY - Screen Y coordinate (clientY)
 * @param {string} [parentId] - Optional parent node ID
 * @param {Object} options - Options with state accessors
 * @returns {{x: number, y: number}} Coordinates relative to parent's center
 */
export function screenToWorld(screenX, screenY, parentId, options) {
  const { state, physics } = options;

  const canvas = state.getCanvas();
  const bState = state.getBState();
  const currentState = state.getCurrentState();
  const physicsNodes = physics.getPhysicsNodes();

  if (!canvas) return { x: 0, y: 0 };
  const rect = canvas.getBoundingClientRect();

  // Step 1: Screen -> World (remove canvas offset, pan, and zoom)
  const worldX = (screenX - rect.left - bState.panX) / bState.zoom;
  const worldY = (screenY - rect.top - bState.panY) / bState.zoom;

  // Step 2: World -> Parent-relative
  // Canvas center is the anchor point for top-level bubbles
  const cx = rect.width / 2;
  const cy = rect.height / 2;

  // If parent is a nested bubble, add its position offset
  let parentOffsetX = 0;
  let parentOffsetY = 0;

  if (parentId && parentId !== currentState.focusId) {
    const parentPhys = physicsNodes.find((n) => n.id === parentId);
    if (parentPhys) {
      // Walk up the parent chain to accumulate all offsets
      let p = parentPhys;
      while (p) {
        parentOffsetX += p.x;
        parentOffsetY += p.y;
        p = p.parent;
      }
    }
  }

  return {
    x: worldX - cx - parentOffsetX,
    y: worldY - cy - parentOffsetY,
  };
}

// ============================================================================
// DRAG HANDLING
// ============================================================================

/**
 * Setup drag behavior for a bubble
 * @param {HTMLElement} el - Bubble element
 * @param {Object} node - Data node
 * @param {Object} pNode - Physics node
 * @param {Object} options - Options with state and physics accessors
 */
export function setupBubbleDrag(el, node, pNode, options) {
  const { state, physics } = options;
  // The bubble's physics node: a render that keeps the element gives it a
  // new one (render.js, el._pNode), read when a drag starts.
  let p = pNode;

  let dragging = false;
  let dragStarted = false;
  let startX, startY, origX, origY;
  let dropTarget = null;
  let willEject = false;
  let ctrlHeld = false;
  const DRAG_THRESHOLD = 5;

  const container = state.getContainer();
  const hint = container.querySelector("#b-hint");
  let originalHintContent = null;
  let originalParent = null; // Store original parent element for bubble
  let dragAbsX = 0,
    dragAbsY = 0; // Absolute position when drag starts

  el.addEventListener("mousedown", (e) => {
    if (e.button !== 0) return;
    // Disable dragging for read-only viewers
    const callbacks = state.getCallbacks();
    if (callbacks.isReadOnly && callbacks.isReadOnly()) {
      return;
    }
    e.stopPropagation();
    dragging = true;
    dragStarted = false;
    ctrlHeld = e.ctrlKey || e.metaKey;

    p = el._pNode || pNode;
    startX = e.clientX;
    startY = e.clientY;
    origX = p.x;
    origY = p.y;

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("keydown", onKey);
    document.body.classList.add("dragging-node");
  });

  // Esc cancels the drag: the bubble goes back where it was, nothing changes.
  // The click that ends a drag is not a selection: Firefox sends one to the
  // dropped bubble, Chromium does not. Only that gesture's click: the next
  // press clears the mark.
  function endGesture() {
    el._dragGesture = true;
    window.addEventListener("mousedown", () => { el._dragGesture = false; }, { capture: true, once: true });
  }

  function onKey(e) {
    if (e.key !== "Escape" || !dragging) return;
    e.preventDefault();
    e.stopPropagation();
    const dragLayer = state.getDragLayer();
    dragging = false;
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
    document.removeEventListener("keydown", onKey);
    document.body.classList.remove("dragging-node");
    if (!dragStarted) return;
    dragStarted = false;
    endGesture();
    el.classList.remove("dragging", "eject-hint");
    if (hint) {
      hint.classList.remove("dragging");
      if (originalHintContent) {
        hint.innerHTML = originalHintContent;
        originalHintContent = null;
      }
    }
    if (originalParent && el.parentElement === dragLayer) {
      originalParent.appendChild(el);
      originalParent = null;
    }
    p.x = origX;
    p.y = origY;
    p.vx = 0;
    p.vy = 0;
    // The drag wrote the transform itself: drop the cached one so the bubble
    // is really put back.
    p._bx = p._by = undefined;
    physics.updateBubblePos(p);
    Titles.schedule();
    container.querySelectorAll(".bubble.drop-target").forEach((b) => b.classList.remove("drop-target"));
    dropTarget = null;
    willEject = false;
  }

  function onMove(e) {
    if (!dragging) return;

    const bState = state.getBState();
    const dragLayer = state.getDragLayer();
    const callbacks = state.getCallbacks();

    ctrlHeld = e.ctrlKey || e.metaKey;

    const mouseDx = Math.abs(e.clientX - startX);
    const mouseDy = Math.abs(e.clientY - startY);
    const mouseDist = Math.sqrt(mouseDx * mouseDx + mouseDy * mouseDy);

    if (!dragStarted && mouseDist > DRAG_THRESHOLD) {
      dragStarted = true;
      el.classList.add("dragging");

      physics.cancelPhysics();

      if (hint) {
        originalHintContent = hint.innerHTML;
        hint.textContent = ctrlHeld
          ? t("views.bubble.restructureMode")
          : t("views.bubble.moveMode");
        hint.classList.add("dragging");
      }

      // Move the bubble to the drag layer (above the other bubbles)
      if (dragLayer) {
        originalParent = el.parentElement;

        // Calculate absolute position of bubble center
        dragAbsX = p.offsetX + p.x;
        dragAbsY = p.offsetY + p.y;
        let parent = p.parent;
        while (parent) {
          dragAbsX += parent.offsetX + parent.x - parent.radius;
          dragAbsY += parent.offsetY + parent.y - parent.radius;
          parent = parent.parent;
        }

        // Move bubble to drag layer with absolute positioning
        dragLayer.appendChild(el);
        el.style.transform = `translate(${dragAbsX - p.radius}px, ${dragAbsY - p.radius}px)`;
      }
    }

    if (!dragStarted) return;

    const dx = (e.clientX - startX) / bState.zoom;
    const dy = (e.clientY - startY) / bState.zoom;
    p.x = origX + dx;
    p.y = origY + dy;

    // Update position - bubble is in drag layer so use absolute coords
    if (dragLayer && el.parentElement === dragLayer) {
      const newAbsX = dragAbsX + dx;
      const newAbsY = dragAbsY + dy;
      el.style.transform = `translate(${newAbsX - p.radius}px, ${newAbsY - p.radius}px)`;
    } else {
      physics.updateBubblePos(p);
    }
    // The titles (titles.js, drawn from x/y) follow the bubble.
    Titles.schedule();

    if (ctrlHeld) {
      if (hint)
        hint.textContent = t("views.bubble.restructureMode");

      // Check eject
      if (p.parent) {
        const dist = Math.hypot(p.x, p.y);
        const ejectThreshold = p.parent.radius + 10;
        const returnThreshold = p.parent.radius - 5;

        if (!willEject && dist + p.radius > ejectThreshold) {
          willEject = true;
          el.classList.add("eject-hint");
        } else if (willEject && dist + p.radius < returnThreshold) {
          willEject = false;
          el.classList.remove("eject-hint");
        }
      }

      // Check drop targets
      dropTarget = null;
      container.querySelectorAll(".bubble").forEach((b) => {
        b.classList.remove("drop-target");
        if (b === el || b.contains(el) || el.contains(b)) return;
        // Moving the multi-selection: selected bubbles are not targets.
        if (MultiSelect.movesSelection(node.id) && MultiSelect.has(b.dataset.id)) return;
        if (b.dataset.id === node.id || isDescendant(node.id, b.dataset.id, callbacks.find))
          return;

        const rect = b.getBoundingClientRect();
        const bcx = rect.left + rect.width / 2;
        const bcy = rect.top + rect.height / 2;
        const dist = Math.hypot(e.clientX - bcx, e.clientY - bcy);

        if (dist < (rect.width / 2) * 0.8) {
          dropTarget = b;
          b.classList.add("drop-target");
        }
      });
    } else {
      if (hint) hint.textContent = t("views.bubble.moveMode");
      willEject = false;
      dropTarget = null;
      el.classList.remove("eject-hint");
      container.querySelectorAll(".bubble").forEach((b) => {
        b.classList.remove("drop-target");
      });
    }
  }

  function onUp(e) {
    const dragLayer = state.getDragLayer();
    const currentDocId = state.getCurrentDocId();
    const callbacks = state.getCallbacks();

    if (!dragStarted) {
      dragging = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("dragging-node");
      return;
    }

    dragging = false;
    dragStarted = false;
    endGesture();
    el.classList.remove("dragging", "eject-hint");
    if (hint) {
      hint.classList.remove("dragging");
      if (originalHintContent) {
        hint.innerHTML = originalHintContent;
        originalHintContent = null;
      }
    }
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
    document.removeEventListener("keydown", onKey);
    document.body.classList.remove("dragging-node");

    // Move the bubble back into its parent
    if (originalParent && el.parentElement === dragLayer) {
      originalParent.appendChild(el);
      // Reset to relative positioning
      physics.updateBubblePos(p);
      originalParent = null;
    }

    p.vx = 0;
    p.vy = 0;

    let restructured = false;
    if (ctrlHeld) {
      if (dropTarget) {
        dropTarget.classList.remove("drop-target");
        const targetId = dropTarget.dataset.id;
        if (MultiSelect.movesSelection(node.id)) {
          // Nothing moved (refused): settle the dropped bubble as usual.
          restructured = MultiSelect.moveSelectionTo(targetId).moved > 0;
        } else if (callbacks.onMoveNode) {
          restructured = callbacks.onMoveNode(node.id, targetId, -1) === true;
        }
      } else if (willEject && p.parent) {
        // Eject to parent level (the whole multi-selection when it's part of it)
        const parentNode = callbacks.findParent(node.id);
        if (parentNode) {
          const grandparent = callbacks.findParent(parentNode.id);
          if (grandparent && MultiSelect.movesSelection(node.id)) {
            restructured = MultiSelect.moveSelectionTo(grandparent.id).moved > 0;
          } else if (grandparent && callbacks.onMoveNode) {
            restructured = callbacks.onMoveNode(node.id, grandparent.id, -1) === true;
          }
        }
      } else {
        // Save position to localStorage (per-user, not synced)
        setBubblePosition(currentDocId, node.id, p.x, p.y, p.radius, p.parentId);
      }
    } else {
      // Just save position to localStorage (per-user, not synced)
      setBubblePosition(currentDocId, node.id, p.x, p.y, p.radius, p.parentId);
    }

    // The Store update rebuilds the new parent relationships. Settling the old
    // graph here can push/save the moved node back into its previous parent.
    if (restructured) return;

    // Scoped: settle only the dragged node's neighborhood — a full
    // re-simulation on every drag release contracted the whole map.
    physics.startPhysics({
      currentDocId,
      dirtyIds: [node.id],
    });
  }
}

// ============================================================================
// ZOOM CONTROLS
// ============================================================================

/**
 * Zoom by a factor around the middle of what is visible (the notes pane may
 * cover the canvas's right side): what is there stays there. The buttons
 * zoomed around the canvas's top-left corner, so the view slid right and
 * down at each step in (Alex, 2026-10-15).
 */
function zoomAroundVisibleCenter(state, factor) {
  const t = state.getBState();
  const zoom = state.clampZoom(t.zoom * factor);
  const canvas = state.getCanvas();
  if (!canvas || zoom === t.zoom) return;
  const rect = canvas.getBoundingClientRect();
  const visible = visibleViewRect(canvas);
  const cx = visible ? visible.x - rect.left - canvas.clientLeft : rect.width / 2;
  const cy = visible ? visible.y - rect.top - canvas.clientTop : rect.height / 2;
  const k = zoom / t.zoom;
  state.setBStateField("zoom", zoom);
  state.setBStateField("panX", cx - (cx - t.panX) * k);
  state.setBStateField("panY", cy - (cy - t.panY) * k);
  state.updateTransform();
}

/**
 * Setup zoom control buttons
 * @param {Object} options - Options with state accessors
 */
export function setupZoomControls(options) {
  const { state } = options;

  const container = state.getContainer();
  const zin = container.querySelector("#b-zin");
  const zout = container.querySelector("#b-zout");
  const zreset = container.querySelector("#b-reset");
  const zfit = container.querySelector("#b-fit");

  if (zin) zin.addEventListener("click", () => zoomAroundVisibleCenter(state, 1.2));
  if (zout) zout.addEventListener("click", () => zoomAroundVisibleCenter(state, 1 / 1.2));

  if (zreset) {
    zreset.addEventListener("click", () => {
      state.setBStateField("zoom", 1);
      state.setBStateField("panX", 0);
      state.setBStateField("panY", 0);
      state.updateTransform();
    });
  }

  if (zfit) {
    zfit.addEventListener("click", () => state.fitToView());
  }
}

// ============================================================================
// CANVAS INTERACTION
// ============================================================================

// Document-level pan handlers — kept at module scope so a remount replaces
// the previous pair instead of stacking a new one (the canvas listeners die
// with the canvas element, but document listeners would survive forever).
let docPanMoveHandler = null;
let docPanUpHandler = null;

/**
 * Setup canvas pan and wheel zoom
 * @param {Object} options - Options with state accessors
 */
export function setupCanvasInteraction(options) {
  const { state } = options;

  const canvas = state.getCanvas();
  const world = state.getWorld();

  if (!canvas) return;

  let isPanning = false;
  let panStartX, panStartY;
  let panningClassTimer = null;

  // Wheel zoom - zoom toward mouse pointer
  canvas.addEventListener(
    "wheel",
    (e) => {
      e.preventDefault();

      const bState = state.getBState();

      // Disable transitions during zoom to prevent jumps/flicker.
      // Single shared removal timer: one add at burst start, one remove
      // 150ms after the last tick. The old per-event add+remove pair made
      // the class flap mid-burst, forcing style recalc over all ~700
      // bubbles several times per second during continuous zooming.
      if (world && !world.classList.contains("panning")) {
        world.classList.add("panning");
      }

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const prevZoom = bState.zoom;
      const newZoom = state.clampZoom(bState.zoom * (e.deltaY > 0 ? 0.9 : 1.1));
      state.setBStateField("zoom", newZoom);

      const zoomRatio = newZoom / prevZoom;
      state.setBStateField("panX", mouseX - (mouseX - bState.panX) * zoomRatio);
      state.setBStateField("panY", mouseY - (mouseY - bState.panY) * zoomRatio);

      state.updateTransform();

      // Re-enable transitions after zoom completes
      // Using 150ms to ensure physics and any pending renders settle first
      if (panningClassTimer) clearTimeout(panningClassTimer);
      panningClassTimer = setTimeout(() => {
        panningClassTimer = null;
        if (world) world.classList.remove("panning");
      }, 150);
    },
    { passive: false },
  );

  // Pan with mouse drag on canvas
  canvas.addEventListener("mousedown", (e) => {
    const bState = state.getBState();

    // Middle mouse button (button 1) always pans, even over nodes
    if (e.button === 1) {
      e.preventDefault();
      isPanning = true;
      panStartX = e.clientX - bState.panX;
      panStartY = e.clientY - bState.panY;
      canvas.style.cursor = "grabbing";
      return;
    }

    // Left mouse button (button 0) only pans on background
    if (e.button === 0 && (e.target === canvas || e.target === world)) {
      isPanning = true;
      panStartX = e.clientX - bState.panX;
      panStartY = e.clientY - bState.panY;
      canvas.style.cursor = "grabbing";
    }
  });

  if (docPanMoveHandler) document.removeEventListener("mousemove", docPanMoveHandler);
  if (docPanUpHandler) document.removeEventListener("mouseup", docPanUpHandler);

  docPanMoveHandler = (e) => {
    if (isPanning) {
      state.setBStateField("panX", e.clientX - panStartX);
      state.setBStateField("panY", e.clientY - panStartY);
      state.updateTransform();
    }
  };
  docPanUpHandler = () => {
    if (isPanning) {
      isPanning = false;
      if (canvas) canvas.style.cursor = "";
    }
  };

  document.addEventListener("mousemove", docPanMoveHandler);
  document.addEventListener("mouseup", docPanUpHandler);

  // Right-click on empty canvas/world (NOT on a bubble) → empty-space menu.
  // Per-bubble contextmenu listeners (in render.js) stopPropagation, so this
  // only fires for genuine background clicks. Routes through the shared
  // onCanvasContextMenu callback, which targets the focused container.
  canvas.addEventListener("contextmenu", (e) => {
    if (e.target !== canvas && e.target !== world) return;
    e.preventDefault();
    const callbacks = state.getCallbacks();
    callbacks?.onCanvasContextMenu?.(e.clientX, e.clientY);
  });
}
